var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable lines-around-comment */
import { v4 as uuidv4 } from 'uuid';
// @ts-ignore
import { getFeatureFlag, REACTIONS_ENABLED } from '../base/flags';
import { getLocalParticipant } from '../base/participants/functions';
// @ts-ignore
import { extractFqnFromPath } from '../dynamic-branding/functions.any';
import { REACTIONS, SOUNDS_THRESHOLDS } from './constants';
import logger from './logger';
/**
 * Returns the queue of reactions.
 *
 * @param {Object} state - The state of the application.
 * @returns {Array}
 */
export function getReactionsQueue(state) {
    return state['features/reactions'].queue;
}
/**
 * Returns chat message from reactions buffer.
 *
 * @param {Array} buffer - The reactions buffer.
 * @returns {string}
 */
export function getReactionMessageFromBuffer(buffer) {
    return buffer.map(reaction => REACTIONS[reaction].message).reduce((acc, val) => `${acc}${val}`);
}
/**
 * Returns reactions array with uid.
 *
 * @param {Array} buffer - The reactions buffer.
 * @returns {Array}
 */
export function getReactionsWithId(buffer) {
    return buffer.map(reaction => {
        return {
            reaction,
            uid: uuidv4()
        };
    });
}
/**
 * Sends reactions to the backend.
 *
 * @param {Object} state - The redux state object.
 * @param {Array} reactions - Reactions array to be sent.
 * @returns {void}
 */
export function sendReactionsWebhook(state, reactions) {
    return __awaiter(this, void 0, void 0, function* () {
        const { webhookProxyUrl: url } = state['features/base/config'];
        const { conference } = state['features/base/conference'];
        const { jwt } = state['features/base/jwt'];
        const { connection } = state['features/base/connection'];
        const jid = connection === null || connection === void 0 ? void 0 : connection.getJid();
        const localParticipant = getLocalParticipant(state);
        const headers = Object.assign(Object.assign({}, jwt ? { 'Authorization': `Bearer ${jwt}` } : {}), { 'Content-Type': 'application/json' });
        const reqBody = {
            meetingFqn: extractFqnFromPath(),
            sessionId: conference.sessionId,
            submitted: Date.now(),
            reactions,
            participantId: localParticipant.jwtId,
            participantName: localParticipant.name,
            participantJid: jid
        };
        if (url) {
            try {
                const res = yield fetch(`${url}/reactions`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(reqBody)
                });
                if (!res.ok) {
                    logger.error('Status error:', res.status);
                }
            }
            catch (err) {
                logger.error('Could not send request', err);
            }
        }
    });
}
/**
 * Returns unique reactions from the reactions buffer.
 *
 * @param {Array} reactions - The reactions buffer.
 * @returns {Array}
 */
function getUniqueReactions(reactions) {
    return [...new Set(reactions)];
}
/**
 * Returns frequency of given reaction in array.
 *
 * @param {Array} reactions - Array of reactions.
 * @param {string} reaction - Reaction to get frequency for.
 * @returns {number}
 */
function getReactionFrequency(reactions, reaction) {
    return reactions.filter(r => r === reaction).length;
}
/**
 * Returns the threshold number for a given frequency.
 *
 * @param {number} frequency - Frequency of reaction.
 * @returns {number}
 */
function getSoundThresholdByFrequency(frequency) {
    for (const i of SOUNDS_THRESHOLDS) {
        if (frequency <= i) {
            return i;
        }
    }
    return SOUNDS_THRESHOLDS[SOUNDS_THRESHOLDS.length - 1];
}
/**
 * Returns unique reactions with threshold.
 *
 * @param {Array} reactions - The reactions buffer.
 * @returns {Array}
 */
export function getReactionsSoundsThresholds(reactions) {
    const unique = getUniqueReactions(reactions);
    return unique.map(reaction => {
        return {
            reaction,
            threshold: getSoundThresholdByFrequency(getReactionFrequency(reactions, reaction))
        };
    });
}
/**
 * Whether or not the reactions are enabled.
 *
 * @param {Object} state - The Redux state object.
 * @returns {boolean}
 */
export function isReactionsEnabled(state) {
    const { disableReactions } = state['features/base/config'];
    if (navigator.product === 'ReactNative') {
        return !disableReactions && getFeatureFlag(state, REACTIONS_ENABLED, true);
    }
    return !disableReactions;
}
